import Vue from 'vue';
import VueI18n from 'vue-i18n';
import axios from "axios";
import EventBus from '@pro/common/eventbus';
import loadScript from '@pro/common/utils/loadScript';
import getInitLocale from '@pro/common/utils/getInitLocale';
import { INDEX_TO_KEY } from '@pro/common/consts/language-options';

Vue.use(VueI18n);
const locale = 'en';
const fallbackLocale = 'en';
let needChangeCurrent = false;
let languageHash = null;
let configLoader = null;

export const i18n = new VueI18n({
  locale,
  fallbackLocale: fallbackLocale,
  silentTranslationWarn: true,
  messages: {}
});

for (const lang of Object.keys(window.cloudLocale || {})) {
  i18n.setLocaleMessage(lang, Object.freeze(window.cloudLocale[lang]));
}

/**
 * 子应用i18n实例集合
 * @type {Map<string, {instance: VueI18n, loadedLanguages: Set<string>}>}
 */
const moduleI18nMap = new Map();

//加载多语言配置
async function loadConfig() {
  if (configLoader) {
    return configLoader;
  }
  configLoader = axios.get(`/language.json?${new Date().getTime()}`).then((val) => {
    if (!val) {
      return Promise.reject();
    }

    let oConfig = val.data;
    if (!oConfig) {
      return Promise.reject();
    }

    if (oConfig.needChangeFiles) {
      needChangeCurrent = oConfig.needChangeFiles.includes('epartner');
    }
    if (oConfig.languageVersion) {
      languageHash = oConfig.languageVersion;
    }
  });

  return configLoader;
}

async function getLocale(module, name, path = 'vendor/locales') {
  try {
    if (name !== 'en') {
      // 英文不需要取这个值，直接复用就好了，不用重复调用接口
      await loadConfig();
      configLoader = null;
    }
  } catch (error) {
    console.log('getLocale_error', error);
    //
  }
  const hash = new Date().getTime();
  //Common是我们自己的组件，hash值使用打包时的或者小语种组件里的。其他的ep子应用使用时间戳
  const url = `./${module}/${path}/${name}.js?hash=${module === 'Common' ? (needChangeCurrent ? (languageHash || window.COMPILATION_HASH) : window.COMPILATION_HASH) : hash}`;
  try {
    await loadScript(url);
  } catch (error) {
    //
  }
  const propName = module === 'Common' ? 'cloudLocale' : `cloudLocale_${module}`;
  return window[propName][name] || {};
}

/**
 * 异步加载i18n实例
 * @param {string} module 子应用名
 * @param {string} curLan 多语言key
 * @param {string} path locales路径
 * @returns i18n实例
 */
export async function i18nModule(module, curLan = i18n.locale, path) {
  path = path || 'vendor/locales';
  let { instance, loadedLanguages } = moduleI18nMap.get(module) || {};

  // 加载备用语言
  const loadFallback = async () => {
    const fallbackMessage = await getLocale(module, fallbackLocale, path);
    return { ...i18n.messages[fallbackLocale], ...fallbackMessage };
  };

  // 如果已经加载过这个子应用的i18n实例，但是备用语言没加载过
  if (loadedLanguages && !loadedLanguages.has(fallbackLocale)) {
    const fallbackMessage = await loadFallback();
    instance.setLocaleMessage(fallbackLocale, Object.freeze(fallbackMessage));
    loadedLanguages.add(fallbackLocale);
  }

  // 如果已经加载过这个子应用的i18n实例，并且当前语言也已经加载过
  if (loadedLanguages?.has(curLan)) {
    instance.locale = curLan;
    return instance;
  }
  // 加载当前模块的语言
  const moduleMessage = await getLocale(module, curLan, path);
  // 合并主应用语言
  let message = { ...i18n.messages[curLan], ...moduleMessage };
  // 如果已经加载过这个子应用的i18n实例，但是当前语言没加载过
  if (instance) {
    instance.setLocaleMessage(curLan, Object.freeze(message));
    instance.locale = curLan;
    loadedLanguages.add(curLan);
    return instance;
  }

  // 没有加载过这个子应用的i18n实例
  const messages = { [curLan]: message };
  // 当前语言不等于备用语言，则还需加载备用语言
  if (curLan !== fallbackLocale) {
    const fallbackMessage = await loadFallback();
    messages[fallbackLocale] = fallbackMessage;
  }
  instance = new VueI18n({
    locale: curLan,
    fallbackLocale: fallbackLocale,
    silentTranslationWarn: true,
    messages
  });
  moduleI18nMap.set(module, {
    instance,
    loadedLanguages: new Set([curLan, fallbackLocale])
  });
  return instance;
}

/**
 * 同步的方式返回子应用已经加载过的i18n实例
 */
export function moduleI18n(module) {
  const moduleI18n = moduleI18nMap.get(module);
  if (moduleI18n?.loadedLanguages.has(i18n.locale)) {
    return moduleI18n.instance;
  }
  throw new Error(`${module} ${i18n.locale} 多语言未加载`);
}

export function setI18nLanguage(lang) {
  EventBus.$emit('change-language-i18n', lang);
  i18n.locale = lang;
  moduleI18nMap.forEach((module, moduleId) => {
    i18nModule(moduleId, lang);
  });
  return lang;
}

/**
 * 主应用已经加载了的语言
 */
const loadedLanguages = new Set();

export async function switchLangAsync(lang) {
  if (typeof lang === 'undefined') {
    lang = getInitLocale();
  }
  if (typeof lang === 'number') {
    lang = INDEX_TO_KEY[lang];
  }
  // 如果从没加载过多语言，并且当前加载的不是英语(备用语言)
  if (loadedLanguages.size === 0 && lang !== fallbackLocale) {
    await switchLangAsync(fallbackLocale);
  }

  // 如果语言已经加载
  if (loadedLanguages.has(lang)) {
    return setI18nLanguage(lang);
  }

  // 如果尚未加载语言
  const messages = await getLocale('Common', lang, 'locales');
  i18n.setLocaleMessage(lang, Object.freeze(messages));
  loadedLanguages.add(lang);
  return setI18nLanguage(lang);
}

export default i18n;
